/**
 * Curated collections mobile slider – dot pagination + scroll sync.
 */
class CuratedCollectionSlider extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    this.scroller = this.querySelector('.custom-collection-grid__items');
    if (!this.scroller) return;

    this.addEventListener('click', this.#onClick);
    this.scroller.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#observeSlides();
    this.#updateActiveDotFromScroll();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.removeEventListener('click', this.#onClick);
    this.scroller?.removeEventListener('scroll', this.#onScroll);
  }

  /** @type {HTMLElement | null} */
  scroller = null;

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const dot = target.closest('.custom-collection-grid__dot');
    if (!dot || !this.contains(dot)) return;

    event.preventDefault();
    event.stopPropagation();

    const index = Number(dot.getAttribute('data-slide-index'));
    if (Number.isNaN(index)) return;

    this.#goTo(index);
  };

  #onScroll = () => {
    window.requestAnimationFrame(() => this.#updateActiveDotFromScroll());
  };

  /** @returns {HTMLElement[]} */
  #getSlides() {
    if (!this.scroller) return [];

    return [...this.scroller.children].filter((child) => {
      if (!(child instanceof HTMLElement)) return false;
      return getComputedStyle(child).display !== 'none';
    });
  }

  /** @param {number} index */
  #goTo(index) {
    const slides = this.#getSlides();
    const slide = slides[index];
    if (!slide || !this.scroller) return;

    const scrollerRect = this.scroller.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const targetLeft = this.scroller.scrollLeft + (slideRect.left - scrollerRect.left);

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.scroller.scrollTo({
      left: targetLeft,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });

    this.#setActiveDot(index);
  }

  #updateActiveDotFromScroll() {
    const slides = this.#getSlides();
    if (!this.scroller || slides.length === 0) return;

    const scrollLeft = this.scroller.scrollLeft;
    let activeIndex = 0;
    let smallestDistance = Infinity;

    const scrollerRect = this.scroller.getBoundingClientRect();

    slides.forEach((slide, index) => {
      const slideLeft = slide.getBoundingClientRect().left - scrollerRect.left + scrollLeft;
      const distance = Math.abs(slideLeft - scrollLeft);

      if (distance < smallestDistance) {
        smallestDistance = distance;
        activeIndex = index;
      }
    });

    this.#setActiveDot(activeIndex);
  }

  #observeSlides() {
    const slides = this.#getSlides();
    if (!this.scroller || slides.length === 0) return;

    this.#observer?.disconnect();

    this.#observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!mostVisible || !(mostVisible.target instanceof HTMLElement)) return;

        const index = slides.indexOf(mostVisible.target);
        if (index >= 0) this.#setActiveDot(index);
      },
      {
        root: this.scroller,
        threshold: [0.55, 0.75, 0.95],
      }
    );

    slides.forEach((slide) => this.#observer?.observe(slide));
  }

  /** @param {number} index */
  #setActiveDot(index) {
    const dots = this.querySelectorAll('.custom-collection-grid__dot');

    dots.forEach((dot, dotIndex) => {
      if (!(dot instanceof HTMLButtonElement)) return;

      const isActive = dotIndex === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }
}

if (!customElements.get('curated-collection-slider')) {
  customElements.define('curated-collection-slider', CuratedCollectionSlider);
}
